import { Timestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';
import React, { Dispatch, useEffect, useState } from 'react';

import { useAppSelector } from '@core/hooks/appHooks';
import { selectVendor } from '@core/store/appSlice/appSlice';
import DemmiCard from '@demmi-ui/Card/Card';
import DemmiCardDivider from '@demmi-ui/Card/CardDivider';
import DemmiInput from '@demmi-ui/Input';
import DemmiModalArea from '@demmi-ui/Modal/ModalArea';
import DemmiSelect from '@demmi-ui/Select/Select';
import { SelectItem } from '@demmi-ui/Select/_types';
import DemmiToggle from '@demmi-ui/Toggle';
import { DemmiFS } from '@subhanhabib/demmilib';

import ModalCreateCouponContentFixed from './ModalCreateCouponContentFixed';
import ModalCreateCouponContentPercentage from './ModalCreateCouponContentPercentage';

interface Props {
  state: number;
  CSSBlock: string;
  setCoupon: React.Dispatch<React.SetStateAction<DemmiFS.Coupon | undefined>>;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
  isDoingRequest: boolean;
}

const ModalCreateCouponContent: React.FC<Props> = ({
  state,
  CSSBlock,
  setCoupon,
  setIsValid,
}) => {
  const vendor = useAppSelector(selectVendor);
  const [type, setType] = useState<DemmiFS.CouponHelper.CouponTypes>(
    DemmiFS.CouponHelper.CouponTypes.coupon_discount_fixed,
  );
  const [codePrefix, setCodePrefix] = useState('');
  const [code, setCode] = useState('');
  const [minSpend, setMinSpend] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [hasStartDate, setHasStartDate] = useState(false);
  const [hasExpirationDate, setHasExpirationDate] = useState(false);
  const [startDate, setStartDate] = useState<Timestamp>();
  const [expirationDate, setExpirationDate] = useState<Timestamp>();
  const [currencySymbol, setCurrencySymbol] = useState('');

  useEffect(() => {
    setIsValid(
      DemmiFS.CouponHelper.isDetailsValid(
        code,
        discount,
        minSpend,
        type,
        hasStartDate ? startDate : undefined,
        hasExpirationDate ? expirationDate : undefined,
      ),
    );
    setCoupon(createCoupon());
  }, [
    code,
    minSpend,
    discount,
    type,
    hasStartDate,
    startDate,
    hasExpirationDate,
    expirationDate,
  ]);

  useEffect(() => {
    if (vendor) {
      setCodePrefix(DemmiFS.CouponHelper.generateCodePrefix(vendor!));
      setCurrencySymbol(
        DemmiFS.CurrencyHelper.getCurrencySymbolFromCountryCode(
          vendor.address.countryCode,
        ),
      );
    }
  }, [vendor]);

  const createCoupon = (): DemmiFS.Coupon => ({
    enabled: true,
    vendorID: vendor!.docID,
    type: type,
    docID: codePrefix + code,
    amount: discount,
    minSpend: minSpend,
    ...(hasStartDate
      ? {
          startDate,
        }
      : {}),
    ...(hasExpirationDate
      ? {
          expirationDate,
        }
      : {}),
  });

  const menuItems = [
    {
      id: DemmiFS.CouponHelper.CouponTypes.coupon_discount_fixed,
      text: DemmiFS.CouponHelper.CouponTypes.coupon_discount_fixed,
      item: DemmiFS.CouponHelper.CouponTypes.coupon_discount_fixed,
    } as SelectItem<DemmiFS.CouponHelper.CouponTypes>,
    {
      id: DemmiFS.CouponHelper.CouponTypes.coupon_discount_percent,
      text: DemmiFS.CouponHelper.CouponTypes.coupon_discount_percent,
      item: DemmiFS.CouponHelper.CouponTypes.coupon_discount_percent,
    } as SelectItem<DemmiFS.CouponHelper.CouponTypes>,
  ];

  const getMenuContent = () => {
    return {
      sections: [
        {
          items: menuItems,
        },
      ],
    };
  };

  const regexCode = (val: string) => {
    const value = val.replace(/[^-_0-9a-zA-Z]+/gi, '').toUpperCase();
    if (val !== value) val = value;
    setCode(val);
  };

  const formatFieldValue = (timestamp?: Timestamp) => {
    if (!timestamp || !DemmiFS.getDateFromFirestore(timestamp)) return;
    return DateTime.fromJSDate(
      DemmiFS.getDateFromFirestore(timestamp!)!,
    ).toFormat('yyyy-MM-dd');
  };

  const updateFieldValue = (
    value: string,
    setter: Dispatch<React.SetStateAction<Timestamp | undefined>>,
  ) => {
    setter(
      !isNaN(Date.parse(value))
        ? Timestamp.fromDate(new Date(`${value} 00:00:00`))
        : undefined,
    );
  };

  return (
    <>
      <DemmiModalArea isDark>
        <div className={`${CSSBlock}__title`}>Create a new coupon</div>
        <div className={`${CSSBlock}__content`}>
          <DemmiCard>
            <DemmiSelect
              content={getMenuContent()}
              value={type || '-'}
              onChange={c => {
                setMinSpend(0);
                setDiscount(0);
                setType(menuItems.find(m => m.id === c)!.item);
              }}
            />
            <br />
            <div className={`${CSSBlock}__code-row`}>
              <div className={`${CSSBlock}__code-prefix`}>{codePrefix}</div>
              <DemmiInput
                type="text"
                placeholder="Coupon Code"
                value={code}
                onChangeValue={(val: string) => regexCode(val)}
              />
            </div>
            <DemmiCardDivider />
            {type === DemmiFS.CouponHelper.CouponTypes.coupon_discount_fixed ? (
              <ModalCreateCouponContentFixed
                CSSBlock={CSSBlock}
                currencySymbol={currencySymbol}
                minSpend={minSpend}
                setMinSpend={setMinSpend}
                discount={discount}
                setDiscount={setDiscount}
              />
            ) : type ===
              DemmiFS.CouponHelper.CouponTypes.coupon_discount_percent ? (
              <ModalCreateCouponContentPercentage
                CSSBlock={CSSBlock}
                currencySymbol={currencySymbol}
                minSpend={minSpend}
                setMinSpend={setMinSpend}
                discount={discount}
                setDiscount={setDiscount}
              />
            ) : null}
            <DemmiCardDivider />
            <div className={`${CSSBlock}__toggle-item`}>
              <DemmiToggle
                isActive={hasStartDate}
                text="Start Date?"
                onToggle={v => setHasStartDate(v)}
              />
              {hasStartDate && (
                <>
                  <DemmiInput
                    type="date"
                    value={formatFieldValue(startDate)}
                    onChangeValue={(val: string) =>
                      updateFieldValue(val, setStartDate)
                    }
                  />
                  <br />
                  <br />
                </>
              )}
            </div>
            <div className={`${CSSBlock}__toggle-item`}>
              <DemmiToggle
                isActive={hasExpirationDate}
                text="Expiration Date?"
                onToggle={v => setHasExpirationDate(v)}
              />
              {hasExpirationDate && (
                <DemmiInput
                  type="date"
                  value={formatFieldValue(expirationDate)}
                  onChangeValue={(val: string) =>
                    updateFieldValue(val, setExpirationDate)
                  }
                />
              )}
            </div>
            {state === -1 && (
              <div className={`${CSSBlock}__error`}>
                Code already exists. Please try another.
              </div>
            )}
          </DemmiCard>
        </div>
      </DemmiModalArea>
    </>
  );
};

export default ModalCreateCouponContent;
